import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  viewChildren,
  ViewChildren,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { auditTime, finalize, firstValueFrom, Observable, Subject } from 'rxjs';
import { Ambito } from 'src/app/services/ambiti/ambiti.service';
import { AziendaService } from 'src/app/services/azienda/azienda.service';
import { CompilaTematiche } from 'src/app/services/report-vsme/report-vsme.service';
import {
  isTematicaQuestionario,
  OpzioneTematica,
  Tematica,
} from 'src/app/services/tematiche/tematiche.service';
import {
  UnitaDiMisura,
  UnitaDiMisuraService,
} from 'src/app/services/unita-di-misura/unita-di-misura.service';
import { UtenteIncaricatoService } from 'src/app/services/utente/utente-incaricato.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { SwiperContainer } from 'swiper/element';
import { Filters } from '../lista-tabellare/classes/lista-tabellare-data-source';
import { SpinnerOverlayComponent } from '../spinner-overlay/spinner-overlay.component';
import {
  selezionaRispostaQuestionario,
  sottorisposteDaDeselezionare,
} from './selezione-questionario';

@Component({
  selector: 'app-compila-assessment',
  standalone: false,

  templateUrl: './compila-assessment.component.html',
  styleUrl: './compila-assessment.component.scss',
})
export class CompilaAssessmentComponent implements AfterViewInit, OnDestroy {
  @Output() completato: EventEmitter<boolean> = new EventEmitter();

  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;
  @ViewChild('swiperAmbiti') swiperAmbiti!: ElementRef<SwiperContainer>;
  @ViewChildren('swiperTematiche') swiperTematiche!: QueryList<ElementRef<SwiperContainer>>;

  // 1. viewChildren signal per ottenere la lista di tutte le textarea con #textArea
  private readonly textAreas = viewChildren<ElementRef<HTMLTextAreaElement>>('textArea');

  // 2. Lista di textarea che si osservano il cambiamento, lista per poi disattivarle tutte nel destroy
  private observers: ResizeObserver[] = [];
  // 3. Subject che viene notificato quando una textarea cambia, cosi da aggiornare l'altezza dello swiper con debounce e evitare sovraccarichi
  private readonly resizeSubject = new Subject<void>();

  private readonly utenteIncaricatoService = inject(UtenteIncaricatoService);

  private _salva?: (
    idModulo: string,
    compilazione: CompilaTematiche | undefined,
  ) => Observable<boolean>;

  @Input()
  set salva(
    salva: (idModulo: string, compilazione: CompilaTematiche | undefined) => Observable<boolean>,
  ) {
    this._salva = salva;
  }

  private _compilazione: CompilaTematiche | undefined;
  @Input()
  set compilazione(compilazione: CompilaTematiche | undefined) {
    this.isReady = false;
    this._compilazione = compilazione;

    this.totTematiche = 0;

    this.ambiti.forEach((ambito, idxTema) => {
      this.totTematiche += ambito.tematiche.length;
    });
    this._updateSwiperHeight();
  }

  public isReady: boolean = false;

  get compilazione() {
    return this._compilazione!;
  }

  private _idModulo!: string;

  @Input()
  set idModulo(idModulo: string) {
    this._idModulo = idModulo;
  }

  get idModulo() {
    return this._idModulo;
  }

  private _disableForm: boolean = false;

  @Input()
  set disableForm(disableForm: boolean) {
    this._disableForm = disableForm;
  }

  get disableForm() {
    return this._disableForm;
  }

  private _temporaryToken: string | undefined = undefined;

  @Input()
  set temporaryToken(temporaryToken: string | undefined) {
    this._temporaryToken = temporaryToken;
  }

  get temporaryToken() {
    return this._temporaryToken;
  }

  get ambiti(): Ambito[] {
    return this.compilazione?.ambiti || [];
  }

  get isIntegrazioneAI() {
    return this.utenteIncaricatoService.isIntegrazioneIA();
  }

  private readonly destroyRef = inject(DestroyRef);

  public currentSlideIndex: number = 0;
  public totTematiche = 0;
  public titoloBtnAvanti = 'Avanti';
  public indexAmbitoCorrente = 0;
  public indexTematicaCorrente = 0;
  public mappaPunteggioAmbiti: { [key: string]: any } = {};

  public arrayUm: { [idKpi: string]: UnitaDiMisura[] } = {};

  constructor(
    private readonly utilityService: UtilityService,
    private readonly unitaDiMisuraService: UnitaDiMisuraService,

    private readonly cdRef: ChangeDetectorRef,
    private readonly aziendaService: AziendaService,
    public dialog: MatDialog,
    private readonly _bottomSheet: MatBottomSheet,
  ) {
    this.resizeSwiperAfterTextAreaResized();
  }

  ngOnDestroy() {
    this.cleanupObservers();
  }

  ngAfterViewInit(): void {
    this.isReady = false;

    this.initSwiper();
    this.cdRef.detectChanges();
    this.isReady = true;
  }

  initSwiper() {
    setTimeout(() => {
      if (this.disableForm) {
        this.swiperAmbiti.nativeElement.swiper.slideTo(0);
      } else {
        this.moveToTematicaSenzaRisposte();
      }
    }, 0);
    this._getUnitaDiMisuraPerKpi();
  }

  /**
   * Metodo che cicla tutte le tematiche e trova la prima tematica senza risposte, se è presente, altrimenti ritorna undefined
   * Se la risposta della temtaica è checkata, allora controllo anche che abbia alemno una sotto risposta checkata, altrimenti la tematica viene considerata senza risposte
   * @returns
   */
  private findTematicaSenzaRisposte(): {
    indiceAmbito?: number;
    indiceTematica?: number;
    currentSlideIndex: number;
  } {
    // 1. Guard, se già pubblicato, non serve controllare
    if (this._compilazione?.stato === 'PUBBLICATO') {
      return { indiceAmbito: undefined, indiceTematica: undefined, currentSlideIndex: 0 };
    }

    let currentSlideIndex = 0;

    for (let idxAmb = 0; idxAmb < this.ambiti.length; idxAmb++) {
      const tematiche = this.ambiti[idxAmb].tematiche || [];

      // 2. Troviamo l'indice della prima tematica che fallisce la validazione
      const idxTemaIncompleto = tematiche.findIndex((tema) => {
        const haRisposte = !!tema.risposte?.length;

        return !haRisposte;
      });

      // 3. Se l'abbiamo trovata, restituiamo i dati per navigare direttamente a quella tematica
      if (idxTemaIncompleto !== -1) {
        return {
          indiceAmbito: idxAmb,
          indiceTematica: idxTemaIncompleto,
          currentSlideIndex: currentSlideIndex + idxTemaIncompleto,
        };
      }

      // 4. Incrementiamo il contatore globale delle slide solo se l'ambito è "completo"
      currentSlideIndex += tematiche.length;
    }

    return { indiceAmbito: undefined, indiceTematica: undefined, currentSlideIndex: 0 };
  }

  public moveToTematicaSenzaRisposte(): boolean {
    const target = this.findTematicaSenzaRisposte();

    // Se indiceAmbito è undefined, significa che tutte le tematiche hanno risposte
    if (target.indiceAmbito === undefined) {
      return false;
    }

    // Aggiorniamo l'indice globale (usando il valore calcolato dal finder)
    this.currentSlideIndex = target.currentSlideIndex;

    // 1. Gestione Swiper Padre (Ambiti)
    const swiperPadre = this.swiperAmbiti?.nativeElement?.swiper;
    if (swiperPadre) {
      swiperPadre.slideTo(target.indiceAmbito);
    }

    // 2. Gestione Swiper Figlio (Tematiche all'interno dell'ambito)
    const swiperFiglioRef = this.swiperTematiche.get(target.indiceAmbito);
    if (swiperFiglioRef) {
      const swiperFiglio = swiperFiglioRef.nativeElement.swiper;
      swiperFiglio.slideTo(target.indiceTematica || 0);

      // Sincronizziamo l'indice locale
      this.indexTematicaCorrente = swiperFiglio.realIndex;
    }

    return true; // Indica che è stata trovata una tematica vuota e ci siamo spostati
  }

  /**
   * Gestione per la visualizzazione della categoria e unità di misura dei kpi.
   */
  private _getUnitaDiMisuraPerKpi(): void {
    // 1. Estraggo tutte le sotto-opzioni in una lista piatta per eliminare i cicli annidati
    const tutteLeSottoOpzioni = this.ambiti
      .flatMap((ambito) => ambito.tematiche || [])
      .flatMap((tematica) => tematica.opzioni || [])
      .flatMap((opzione) => opzione.sottoOpzioni || []);

    for (const sottoOpzione of tutteLeSottoOpzioni) {
      const { idCategoriaUnitaDiMisura: idCat, tipo } = sottoOpzione;

      // 2. Guard: salto se non è un KPI o manca l'ID categoria
      if (tipo !== 'kpi' || !idCat) continue;

      // 3. Ripristino dati danneggiati
      this._assicuraValoriKpiMinimi(sottoOpzione);

      // 4. Caricamento Unità di Misura
      if (!this.arrayUm[idCat]) {
        this._caricaDatiUnitaDiMisura(idCat);
      }
    }
  }

  /**
   * Inizializza un valore KPI di default se l'assessment risulta danneggiato/vuoto.
   * Capitava alcune volte che un sincronizzatore faceva casino, quindi
   * se trovo un oggetto "valore_kpi" non definito, lo inizializzo io di default
   */
  private _assicuraValoriKpiMinimi(sottoOpzione: any): void {
    let haEsercizio = false;
    let anno: number | undefined;
    const haValoriVuoti = !sottoOpzione.valoriKpi?.length;

    if (this._compilazione?.esercizio?.length) {
      haEsercizio = this._compilazione?.esercizio?.length > 0;
      anno = Number.parseInt(this._compilazione.esercizio[0], 10);
    }

    if (haValoriVuoti && haEsercizio) {
      sottoOpzione.valoriKpi.push({
        anno,
        idCategoriaUnitaDiMisura: sottoOpzione.idCategoriaUnitaDiMisura,
        idUnitaDiRiferimento: sottoOpzione.idUnitaDiRiferimento,
        tipo: 'valore_kpi',
        valoreAttribuito: undefined,
      });
    }
  }

  /**
   * Gestisce la chiamata al servizio e lo stato dello spinner per una specifica categoria.
   */
  private _caricaDatiUnitaDiMisura(idCategoria: string): void {
    // Evito chiamate doppie per la stessa categoria mentre la prima è in corso
    this.arrayUm[idCategoria] = [];

    const filters: Filters[] = [
      {
        chiave: 'idCategoriaUnitaDiMisura',
        operator: 'eq',
        values: [idCategoria],
      },
    ];

    const serviceCall$ = this._temporaryToken
      ? this.unitaDiMisuraService.getUnitaDiMisuraConTempToken(
          this._temporaryToken,
          0,
          1000,
          '',
          filters,
        )
      : this.unitaDiMisuraService.getUnitaDiMisura(0, 1000, '', filters);

    this.spinnerOver.show();

    serviceCall$.pipe(finalize(() => this.spinnerOver.hide())).subscribe((res) => {
      this.arrayUm[idCategoria] = res?.content || [];
    });
  }

  /**
   * Mi creo una mappa di tipo ['idAmbito' : punteggio] per visualizzare il
   * punteggio in tempo reale nel momento della compilazione dell'assessment
   * @param compilazione
   * @returns
   */
  valorizzaMappaPunteggio(compilazione: CompilaTematiche) {
    let mappaPunteggio: { [key: string]: any } = {};

    compilazione?.ambiti.forEach((ambito: any) => {
      mappaPunteggio[ambito.id] = ambito?.punteggioPercentuale;
    });

    this.mappaPunteggioAmbiti = mappaPunteggio;
  }

  onRadioChange(event: MatRadioChange, idxAmb: number, idxTem: number) {
    if (event instanceof MatRadioChange) {
      //Aggiunto per risolvere il glitch di visualizzazione, prendeva un event non di tipo MatRadioChange

      this.ambiti[idxAmb].tematiche[idxTem].risposte = [event.value];

      this.ambiti[idxAmb].tematiche[idxTem].opzioni.forEach((opt, idx) => {
        if (opt.chiave !== event.value) {
          this.ambiti[idxAmb].tematiche[idxTem].opzioni[idx].sottoRisposte = [];
        }
      });

      //risposte
      this._updateSwiperHeight();
    }
  }

  /**
   * Gestisce la selezione (check) di un'opzione in un sistema a livelli gerarchici.
   * * - Se l'indice è inferiore al livello minimo (`minLvRisp`), resetta le altre risposte
   * mantenendo solo quella attuale (comportamento tipo radio).
   * - Se l'indice è superiore o uguale, garantisce che tutti i livelli intermedi tra il
   * minimo e l'attuale siano selezionati, rimuovendo quelli al di sotto della soglia minima.
   *
   * @param indexRisp - Indice dell'opzione selezionata nell'array delle opzioni.
   * @param minLvRisp - Indice soglia che separa le opzioni esclusive da quelle cumulative.
   * @param tematica  - L'oggetto contenente le opzioni disponibili e l'array delle risposte da aggiornare.
   * @param opz       - L'oggetto opzione specifico che è stato selezionato.
   */
  private _checkMultiLivello(
    indexRisp: number,
    minLvRisp: number,
    tematica: Tematica,
    opz: any,
  ): void {
    const opzioni = tematica.opzioni ?? [];
    let risposteSet = new Set(tematica.risposte ?? []);

    if (indexRisp < minLvRisp) {
      risposteSet = new Set([opz.chiave]);
    } else {
      opzioni.slice(0, minLvRisp).forEach((o) => risposteSet.delete(o.chiave));

      for (let i = minLvRisp; i <= indexRisp; i++) {
        risposteSet.add(opzioni[i].chiave);
      }
    }

    tematica.risposte = Array.from(risposteSet);
  }

  /**
   * Gestisce la deselezione (de-check) di un'opzione in un sistema a livelli gerarchici.
   * * Il metodo implementa una deselezione "a cascata": se un livello viene rimosso,
   * vengono rimossi automaticamente anche tutti i livelli gerarchicamente superiori (successivi).
   * Non esegue operazioni se l'indice è inferiore al livello minimo di soglia.
   *
   * @param indexRisp - Indice dell'opzione che si sta deselezionando.
   * @param minLvRisp - Indice soglia sotto il quale la deselezione non ha effetto sulla gerarchia.
   * @param tematica  - L'oggetto contenente le opzioni e l'array delle risposte.
   * @param opz       - L'oggetto opzione specifico che è stato deselezionato.
   */
  private _deCheckMultiLivello(
    indexRisp: number,
    minLvRisp: number,
    tematica: Tematica,
    opz: any,
  ): void {
    if (indexRisp < minLvRisp || !tematica.risposte) return;

    const opzioni = tematica.opzioni ?? [];
    const risposteSet = new Set(tematica.risposte);

    // Rimuove l'opzione attuale e tutte le successive nella gerarchia
    opzioni.slice(indexRisp).forEach((o) => risposteSet.delete(o.chiave));

    tematica.risposte = Array.from(risposteSet);
  }

  /**
   * Metodo check opzione
   * @param event
   * @param tematica
   * @param opz
   * @returns
   */
  cambioCheckOpzione(event: any, tematica: Tematica, opz: any) {
    if (isTematicaQuestionario(tematica)) {
      this._cambioCheckOpzioneQuestionario(event.checked, tematica, opz);
      this._updateSwiperHeight();
      return;
    }

    tematica.risposte ??= [];

    const indexRisp = tematica.opzioni.findIndex((opzList) => opzList.chiave === opz.chiave);

    const idx = tematica.risposte.indexOf(opz.chiave);
    const minLvRisp = (tematica.minLivelloRispostaAutomatica ?? 3) - 1;

    if (event.checked) {
      // CHECKATO

      if (idx < 0) {
        tematica.risposte.push(opz.chiave);
      }

      this._checkMultiLivello(indexRisp, minLvRisp, tematica, opz);
    } else {
      // DE-CHECKATO

      if (idx >= 0) {
        tematica.risposte.splice(idx, 1);
      }

      this._deCheckMultiLivello(indexRisp, minLvRisp, tematica, opz);
    }

    this._updateSwiperHeight();
  }

  sottoOpzAzioneKpiChecked(event: any, opzSec: any, tematica: Tematica, opzPadre: OpzioneTematica) {
    if (isTematicaQuestionario(tematica)) {
      // Risposta a scelta singola: selezionare una sottorisposta deseleziona le sorelle
      sottorisposteDaDeselezionare(opzPadre, opzSec, event.checked).forEach(
        (sorella) => (sorella.checked = false),
      );
    }

    this.cambioCheckSottoOpzione(event, opzSec);
    this._updateSwiperHeight();
  }

  /**
   * Selezione di una risposta in modalità Questionario: singola o multipla secondo il
   * flag multirisposta della tematica, NC esclusiva, nessuna logica a soglia.
   * Le risposte che escono dalla selezione perdono anche le sottorisposte selezionate.
   */
  private _cambioCheckOpzioneQuestionario(checked: boolean, tematica: Tematica, opz: any): void {
    const esito = selezionaRispostaQuestionario(tematica, opz, checked);

    tematica.risposte = esito.risposte;

    tematica.opzioni
      .filter((opzione) => esito.rimosse.includes(opzione.chiave))
      .forEach((opzione) => {
        (opzione.sottoOpzioni ?? []).forEach((sotto) => (sotto.checked = false));
      });
  }

  cambioCheckSottoOpzione(event: any, opzSec: any) {
    opzSec.checked = event.checked;
  }

  checkSotto(sottoRisposte: string[] | undefined, chiave: string) {
    const isRisposta = sottoRisposte?.find((risp) => {
      return risp === chiave;
    });

    if (isRisposta) {
      return true;
    } else {
      return false;
    }
  }

  onSlideChange() {
    this.indexAmbitoCorrente = this.swiperAmbiti.nativeElement.swiper.realIndex;

    this.indexTematicaCorrente =
      this.swiperTematiche.get(this.indexAmbitoCorrente)?.nativeElement.swiper.realIndex || 0;

    const passateTema = this.ambiti
      .slice(0, this.indexAmbitoCorrente)
      .map((amb) => amb.tematiche.length)
      .reduce((pre, curr) => {
        return pre + curr;
      }, 0);

    this.currentSlideIndex = passateTema + this.indexTematicaCorrente;

    this._updateSwiperHeight();
  }

  /**
   * Gestisce il passaggio alla slide successiva o la pubblicazione finale.
   * Esegue la validazione del form, la conferma dell'utente e il salvataggio remoto.
   */
  async avanti() {
    // 1. Caso base: Navigazione semplice se il form è disabilitato
    if (this.disableForm) {
      return this.avantiSlide();
    }

    // 2. Controllo Finale: Se siamo all'ultima slide, chiediamo conferma
    const isLastSlide = this.currentSlideIndex + 1 === this.totTematiche;
    if (isLastSlide) {
      const confermato = await this._confermaPubblicazione();
      if (!confermato) return;

      if (this.compilazione) this.compilazione.stato = 'PUBBLICATO';
    }

    // 3. Preparazione dati
    if (this.compilazione) {
      this.compilazione.tipo = 'AUTOVALUTAZIONE';
    }

    // 4. Esecuzione salvataggio
    this._eseguiSalvataggio();
  }

  /**
   * Mostra il dialog di conferma e restituisce true se l'utente accetta.
   */
  private async _confermaPubblicazione(): Promise<boolean> {
    const dialog = this.utilityService.openDialog({
      titolo: 'Confermare dati inseriti?',
      descrizione: 'Non sarà più possibile modificarli',
      bottoni: [
        { nome_btn: 'No', id_btn: 'N' },
        { nome_btn: 'Si', id_btn: 'S' },
      ],
    });

    const scelta = await firstValueFrom(dialog.beforeClosed());
    return scelta === 'S';
  }

  /**
   * Gestisce la chiamata al servizio di salvataggio e i relativi stati di errore.
   */
  private _eseguiSalvataggio(): void {
    if (!this._salva) {
      return console.warn('NESSUN METODO SALVATAGGIO PASSATO');
    }

    this._salva(this.idModulo, this.compilazione).subscribe({
      next: (esito) => esito && this.avantiSlide(),
      error: (err) => {
        console.error('ERRORE PUBBLICAZIONE:', err);
        this.utilityService.openDialog({
          titolo: 'Salvataggio non riuscito',
          descrizione: err?.error?.message || 'Errore nel salvataggio',
          bottoni: [{ nome_btn: 'Chiudi' }],
        });
      },
    });
  }

  /**
   * Gestisce l'avanzamento tra le slide delle tematiche e degli ambiti.
   */
  avantiSlide(): void {
    const currentAmbitoIndex = this.swiperAmbiti.nativeElement.swiper.realIndex;
    const swiperTematiche = this.swiperTematiche.get(currentAmbitoIndex)?.nativeElement.swiper;
    const totTematicheInAmbito = this.ambiti[currentAmbitoIndex].tematiche.length;

    // 1. Caso finale: se siamo all'ultima slide assoluta
    if (this.currentSlideIndex + 1 === this.totTematiche && !this.disableForm) {
      this.completato.emit(true);
      return;
    }

    // 2. Navigazione logica
    const isLastTematicaInAmbito = (swiperTematiche?.realIndex || 0) + 1 === totTematicheInAmbito;

    if (isLastTematicaInAmbito) {
      // Fine delle tematiche di questo ambito, passa al prossimo ambito
      this.swiperAmbiti.nativeElement.swiper.slideNext();
    } else {
      // Passa alla prossima tematica nell'ambito corrente
      swiperTematiche?.slideNext();
    }
  }

  /**
   * Gestisce il ritorno alla slide precedente.
   */
  indietro(): void {
    // 1. Caso iniziale: se siamo alla prima slide assoluta
    if (this.currentSlideIndex === 0 && !this.disableForm) {
      this.completato.emit(true);
    }

    const currentAmbitoIndex = this.swiperAmbiti.nativeElement.swiper.realIndex;
    const currentSwiperTematiche =
      this.swiperTematiche.get(currentAmbitoIndex)?.nativeElement.swiper;

    // 2. Navigazione logica
    const isFirstTematicaInAmbito = (currentSwiperTematiche?.realIndex || 0) === 0;

    if (isFirstTematicaInAmbito) {
      // Torna all'ambito precedente
      this.swiperAmbiti.nativeElement.swiper.slidePrev();

      // Una volta cambiato ambito, dobbiamo andare all'ultima tematica dell'ambito precedente
      this._resetLastSlideInPreviousAmbito();
    } else {
      // Torna alla tematica precedente nell'ambito corrente
      currentSwiperTematiche?.slidePrev();
    }
  }

  /**
   * Helper privato per posizionare lo swiper sull'ultima slide dell'ambito precedente.
   */
  private _resetLastSlideInPreviousAmbito(): void {
    const newIndex = this.swiperAmbiti.nativeElement.swiper.realIndex;
    const prevSwiper = this.swiperTematiche.get(newIndex)?.nativeElement.swiper;

    if (prevSwiper) {
      const lastIndex = prevSwiper.slides.length - 1;
      prevSwiper.slideTo(lastIndex);
    }
  }

  /**
   * Update dell'altezza dello swiper, perchè ha un'altezza dinamica in base al contenuto
   */
  private _updateSwiperHeight() {
    setTimeout(() => {
      this.swiperTematiche.forEach((swiper) => {
        swiper.nativeElement.swiper.update();
      });
      this.swiperAmbiti.nativeElement.swiper.update();
    }, 100);
  }

  /**
   * se ho modificato la dimensione della celle della tabella allora devo renderizzare l'altezza dello swiper
   * @param variazioneDimensioneCelle
   */
  renderizzaSwiper(variazioneDimensioneCelle: boolean) {
    if (variazioneDimensioneCelle) {
      this._updateSwiperHeight();
    }
  }

  /**
   * Gestisce il ridimensionamento delle textarea per aggiornare l'altezza degli Swiper.
   */
  private resizeSwiperAfterTextAreaResized() {
    /**
     * Sottoscrive al subject che notifica i ridimensionamenti delle textarea
     * e forza l'aggiornamento degli swiper.
     *
     * La sottoscrizione viene throttled con `auditTime(32)` per evitare
     * chiamate troppo frequenti durante il resize continuo.
     */
    const subscription = this.resizeSubject
      .pipe(auditTime(32))
      .subscribe(() => this.forceSwiperUpdate());

    /**
     * Pulizia automatica della sottoscrizione quando il componente viene distrutto.
     * Utilizza `DestroyRef.onDestroy()` di Angular 19 Signals per evitare memory leak.
     */
    this.destroyRef.onDestroy(() => subscription.unsubscribe());

    /**
     * Effetto reattivo che osserva tutte le textarea correntemente renderizzate.
     * Ogni volta che cambia la lista delle textarea (`this.textAreas()`),
     * vengono:
     *   - rimossi i vecchi ResizeObserver tramite `cleanupObservers()`
     *   - creati nuovi ResizeObserver per ogni textarea
     *   - notificato il subject `resizeSubject` ad ogni cambiamento di dimensione
     *
     * Questo permette agli swiper di aggiornare dinamicamente la loro altezza
     * quando le textarea cambiano dimensione o quando cambiano le slide.
     */
    effect(() => {
      this.cleanupObservers();
      this.textAreas().forEach((ref) => {
        const ro = new ResizeObserver(() => this.resizeSubject.next());
        ro.observe(ref.nativeElement);
        this.observers.push(ro);
      });
    });
  }
  /**
   * Forza l'aggiornamento del layout di tutti gli Swiper (tematiche e ambiti).
   */
  private forceSwiperUpdate() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.swiperTematiche?.forEach((swiper) => {
          swiper.nativeElement.swiper?.update();
        });

        this.swiperAmbiti?.nativeElement.swiper?.update();
      });
    });
  }

  /**
   * Scollega tutti i ResizeObserver attivi per evitare memory leak.
   */
  private cleanupObservers() {
    this.observers.forEach((ro) => ro.disconnect());
    this.observers = [];
  }
}
